// ============================================================================
// Zod schemas validating module JSON content at load time.
// A malformed module fails loudly with a useful error instead of rendering
// "undefined" all over the UI.
// ============================================================================

import { z } from 'zod';

const statBlock = z.object({
  value: z.string(),
  label: z.string(),
});

const frameworkItem = z.object({
  letter: z.string(),
  word: z.string(),
  desc: z.string(),
});

const gridItem = z.object({
  num: z.string(),
  label: z.string(),
});

const exampleStep = z.object({
  role: z.string(),
  text: z.string(),
});

const example = z.object({
  steps: z.array(exampleStep).min(1),
});

export const slideSchema = z.object({
  type: z.enum(['stat', 'framework', 'technique', 'grid', 'points']),
  heading: z.string().min(1),
  subheading: z.string().optional(),
  body: z.string().optional(),
  stat: statBlock.optional(),
  callout: z.string().optional(),
  // `items` may be framework items (with `word`) or grid items (with `num`)
  items: z
    .array(z.union([frameworkItem, gridItem]))
    .optional(),
  points: z.array(z.string()).optional(),
  example: example.optional(),
});

const scenarioOption = z.object({
  label: z.string().min(1),
  consequence: z.string().min(1),
  type: z.enum(['best', 'ok', 'poor']),
});

const scenario = z.object({
  title: z.string().min(1),
  situation: z.string().min(1),
  options: z.array(scenarioOption).min(2),
});

const quizQuestion = z.object({
  question: z.string().min(1),
  options: z.array(z.string()).min(2),
  correct: z.number().int().nonnegative(),
  explanation: z.string().min(1),
});

const resource = z.object({
  tag: z.string().min(1),
  type: z.enum(['video', 'pdf', 'article', 'web']),
  title: z.string().min(1),
  description: z.string().min(1),
  url: z.string().url(),
});

export const moduleContentSchema = z
  .object({
    id: z.string().regex(/^m\d+$/, 'id must look like m0, m1, …'),
    number: z.number().int().nonnegative(),
    title: z.string().min(1),
    subtitle: z.string().min(1),
    duration: z.string().min(1),
    durationMins: z.number().int().positive(),
    learningOutcomes: z.array(z.string().min(1)).min(1),
    slides: z.array(slideSchema).min(1),
    scenario,
    quiz: z.array(quizQuestion).min(1),
    resources: z.array(resource),
  })
  .refine(
    (mod) => mod.scenario.options.some((o) => o.type === 'best'),
    'Each scenario must include exactly one option with type "best"',
  )
  .refine(
    (mod) =>
      mod.quiz.every((q) => q.correct >= 0 && q.correct < q.options.length),
    'Each quiz question\'s `correct` index must point to a valid option',
  );

export const comingSoonModuleSchema = z.object({
  id: z.string(),
  number: z.number().int().nonnegative(),
  title: z.string().min(1),
  eta: z.string().min(1),
});

export const trackConfigSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  storageKey: z.string().regex(/^[a-z0-9_]+$/i),
  headerLabel: z.string().min(1),
  welcomeDescription: z.string().min(1),
  regulationLine: z.string().min(1),
  programmeName: z.string().min(1),
  organisation: z.string().min(1),
  safetyManagerEmail: z.string().email(),
  moduleOrder: z.array(z.string()).min(1),
});

export type ValidatedModule = z.infer<typeof moduleContentSchema>;
export type ValidatedTrackConfig = z.infer<typeof trackConfigSchema>;
